import { forwardRef } from 'react';
import { Link } from 'react-router-dom';
import { Spinner } from './Spinner';

import { useReducedMotion } from '../hooks/useReducedMotion';

const variantStyles = {
    primary: 'bg-emerald-700 text-white shadow-sm hover:bg-emerald-800 focus:ring-emerald-100',
    secondary: 'border border-slate-200 bg-white text-slate-700 shadow-sm hover:border-slate-300 hover:bg-slate-50 focus:ring-slate-100',
    dark: 'bg-gray-900 text-white shadow-sm hover:bg-black focus:ring-gray-100',
    ghost: 'bg-transparent text-gray-700 hover:bg-gray-100 focus:ring-gray-100',
    danger: 'bg-red-600 text-white shadow-sm hover:bg-red-700 focus:ring-red-100',
    'ghost-white': 'bg-transparent text-white hover:bg-white/10 focus:ring-white/20',
    'outline-white': 'border border-white/30 bg-transparent text-white hover:bg-white hover:text-gray-900 focus:ring-white/20',
    'outline-dark': 'border border-gray-200 bg-white/90 text-gray-800 shadow-sm hover:bg-gray-900 hover:text-white focus:ring-gray-100',
    'emerald-light': 'bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100 focus:ring-emerald-100',
};

const sizeStyles = {
    sm: 'px-3 py-2 text-xs gap-1.5',
    md: 'px-5 py-3 text-sm gap-2',
    lg: 'px-6 py-4 text-base gap-2.5',
    'icon-sm': 'h-9 w-9 p-0',
    'icon-md': 'h-11 w-11 p-0',
    'icon-lg': 'h-12 w-12 p-0',
};

export const Button = forwardRef(({
                                      variant = 'primary',
                                      size = 'md',
                                      loading = false,
                                      disabled = false,
                                      fullWidth = false,
                                      icon,
                                      iconPosition = 'left',
                                      children,
                                      className = '',
                                      to,
                                      href,
                                      type = 'button',
                                      ...props
                                  }, ref) => {
    const prefersReducedMotion = useReducedMotion();

    const baseClasses = `
        inline-flex items-center justify-center
        rounded-xl font-semibold
        ${prefersReducedMotion ? '' : 'transition-all duration-200'}
        focus:outline-none focus:ring-4
        disabled:cursor-not-allowed disabled:opacity-60
        active:translate-y-px
        ${variantStyles[variant] || variantStyles.primary}
        ${sizeStyles[size] || sizeStyles.md}
        ${fullWidth ? 'w-full' : ''}
        ${loading ? 'cursor-wait' : ''}
        ${className}
    `;

    const content = (
        <>
            {loading && (
                <Spinner size={size === 'sm' ? 14 : 16}
                         className={variant === 'primary' || variant === 'dark' || variant === 'danger' ? 'border-white/30 border-t-white' : 'border-gray-300 border-t-gray-600'} />
            )}
            {!loading && icon && iconPosition === 'left' && icon}
            {children && <span>{children}</span>}
            {!loading && icon && iconPosition === 'right' && icon}
            {loading && <span className="opacity-70">{children || 'Loading...'}</span>}
        </>
    );

    if (to && !disabled) {
        return (
            <Link to={to} className={baseClasses} ref={ref} {...props}>
                {content}
            </Link>
        );
    }

    if (href && !disabled) {
        return (
            <a href={href} className={baseClasses} ref={ref} {...props}>
                {content}
            </a>
        );
    }

    return (
        <button type={type} className={baseClasses} disabled={disabled || loading} ref={ref} {...props}>
            {content}
        </button>
    );
});

Button.displayName = 'Button';
